import { graphql, PageRendererProps } from 'gatsby';
import React from 'react';
import styled from 'styled-components';
import { AirtableRemoteWorkGearCategoriesEdge, AuthorByIdQuery } from '../../graphql-types';
import { Accordion } from '../components/Accordion';
import { CardAccordion } from '../components/CardAccordion';
import { PageForm } from '../components/Form';
import { CardsGridWrap } from '../components/ItemsGrid';
import { Layout, PageHero, Section } from '../components/Layout';
import { RemoteWorkers } from '../components/RemoteWorkers';
import SEO from '../components/SEO';
import { Strong } from '../components/Strong';
import { useEventTracker } from '../utils/analytics/useEventTracker';
import { floralwhite, gray, primaryColor } from '../utils/styles';

interface Props extends PageRendererProps {
  pageContext: AirtableRemoteWorkGearCategoriesEdge;
  data: AuthorByIdQuery;
}

const TopicDetails = styled.div`
  p:nth-child(4) {
    margin: 0;
  }
`;

const ProductBullets = styled.p`
  white-space: pre-wrap;
`;

const BuyNow = styled.a`
  display: flex;
  justify-content: flex-end;
  color: ${primaryColor};
  font-weight: 500;
  &:hover {
    font-weight: 700;
  }
`;

const CategoryTitle = styled.h3`
  margin-top: 0;
  transition: color 0.3s;
`;

const CategorySubtitle = styled.p`
  color: ${gray};
  margin: 0;
`;

const Category = styled.div`
  width: 100%;
  &:hover ${CategoryTitle} {
    color: ${primaryColor};
  }
`;

const GearCategoryTemplate = ({ data }: Props) => {
  const trackClick = useEventTracker('click');
  const { name, gearItems } = data.gearCategory?.data || {};
  return (
    <Layout>
      <SEO title={name} description={name} />
      <PageHero
        image={<RemoteWorkers />}
        content={
          <TopicDetails>
            <h1>
              Are you happy with your home <Strong>office setup</Strong>?
            </h1>
            <p>
              From the comfort of our office chairs we’ve created this list with everything you need to work from home
              and make your back feel great.
            </p>
            <p>Bonus: since it’s Black Friday, you can expect to find discounts for most of the items.</p>
            <p>Don’t shop too much and enjoy!</p>
          </TopicDetails>
        }
      />
      <Section color={floralwhite}>
        <CardsGridWrap>
          <Accordion>
            {gearItems &&
              gearItems.map(({ data: item }, index) => {
                const [localFile] = item?.picture?.localFiles || [];
                const imageFluid = localFile?.childImageSharp?.fluid;
                return (
                  item && (
                    <CardAccordion key={index} index={index}>
                      <Accordion.Header
                        id={item.amazonProductId}
                        index={index}
                        fluid={imageFluid}
                        onClick={() => trackClick({ label: item.amazonProductId, category: 'card-click' })}
                      >
                        <Category>
                          <CategoryTitle>{item.name}</CategoryTitle>
                          <CategorySubtitle>
                            <Strong>{item.rating}</Strong>/5 | {item.reviews} Reviews
                          </CategorySubtitle>
                        </Category>
                      </Accordion.Header>
                      <Accordion.Section id={item.amazonProductId} index={index}>
                        {item.pros && (
                          <>
                            <h5>Pros</h5>
                            <ProductBullets>{item.pros}</ProductBullets>
                          </>
                        )}
                        {item.cons && (
                          <>
                            <h5>Cons</h5>
                            <ProductBullets>{item.cons}</ProductBullets>
                          </>
                        )}
                        <BuyNow
                          href={item.amazonLink}
                          target="_blank"
                          onClick={() => trackClick({ label: item.amazonProductId, category: 'outbound' })}
                        >
                          BUY NOW
                        </BuyNow>
                      </Accordion.Section>
                    </CardAccordion>
                  )
                );
              })}
          </Accordion>
        </CardsGridWrap>
      </Section>
      <Section color={floralwhite}>
        <PageForm
          endpoint="topic"
          cta="get advice"
          title="What’s keeping you up at night?"
          subtitle="Whatever that is, there are experienced people who figured it out. We find them and share their lessons with you."
          fields={[
            {
              name: 'topic',
              label: 'I want to',
              type: 'text',
              initialValue: '',
              placeholder: 'Make a wish...',
            },
            {
              name: 'email',
              label: 'Send all the advice to',
              type: 'email',
              initialValue: '',
              placeholder: 'Type your email here...',
            },
          ]}
        />
      </Section>
    </Layout>
  );
};

export default GearCategoryTemplate;

export const pageQuery = graphql`
  query GearCategoryById($id: String!) {
    gearCategory: airtableRemoteWorkGearCategories(id: { eq: $id }) {
      data {
        name
        slug
        weight
        picture {
          localFiles {
            childImageSharp {
              fluid(maxWidth: 160) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
        gearItems {
          data {
            amazonLink
            amazonProductId
            name
            pros
            cons
            rating
            reviews
            picture {
              localFiles {
                childImageSharp {
                  fluid(maxWidth: 160) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
            }
          }
        }
      }
      id
      recordId
    }
  }
`;
